import React, { useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Info, X } from "lucide-react";
import SearchInput from "./components/SearchInput";
import MapView from "./components/MapView";
import ResultsPanel from "./components/ResultsPanel";
import AlgorithmInfo from "./components/AlgorithmInfo";

const calculatePolygonCentroid = (coordinates) => {
  let totalArea = 0;
  let centroidX = 0;
  let centroidY = 0;
  
  const n = coordinates.length;
  
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = coordinates[i];
    const [x1, y1] = coordinates[i + 1];
    
    const crossProduct = x0 * y1 - x1 * y0;
    totalArea += crossProduct;
    centroidX += (x0 + x1) * crossProduct;
    centroidY += (y0 + y1) * crossProduct;
  }
  
  totalArea /= 2;
  if (Math.abs(totalArea) < 1e-10) return null;
  
  centroidX /= (6 * totalArea);
  centroidY /= (6 * totalArea);
  
  return { lng: centroidX, lat: centroidY, area: Math.abs(totalArea) };
};

const calculateGeometricCenter = (geojson) => {
  try {
    const polygonCentroids = [];
    
    const extractPolygons = (coords, type) => {
      if (type === 'Polygon') {
        const outerRing = coords[0];
        const centroid = calculatePolygonCentroid(outerRing);
        if (centroid && centroid.area > 0) {
          polygonCentroids.push(centroid);
        }
      } else if (type === 'MultiPolygon') {
        coords.forEach(polygon => {
          const outerRing = polygon[0];
          const centroid = calculatePolygonCentroid(outerRing);
          if (centroid && centroid.area > 0) {
            polygonCentroids.push(centroid);
          }
        });
      }
    };
    
    extractPolygons(geojson.coordinates, geojson.type);
    
    if (polygonCentroids.length === 0) return null;
    
    let totalArea = 0;
    let weightedLat = 0;
    let weightedLng = 0;
    
    polygonCentroids.forEach(c => {
      totalArea += c.area;
      weightedLat += c.lat * c.area;
      weightedLng += c.lng * c.area;
    });
    
    return {
      lat: weightedLat / totalArea,
      lng: weightedLng / totalArea
    };
  } catch (e) {
    console.error('Error calculating centroid:', e);
    return null;
  }
};

const getKeyPoints = (geojson, maxPoints = 16) => {
  const points = [];
  
  const extractPoints = (coords) => {
    coords.forEach(coord => {
      if (Array.isArray(coord[0])) {
        extractPoints(coord);
      } else {
        points.push({ lng: coord[0], lat: coord[1] });
      }
    });
  };
  
  extractPoints(geojson.coordinates);
  
  if (points.length <= maxPoints) return points;
  
  const step = Math.floor(points.length / maxPoints);
  const selected = [];
  for (let i = 0; i < points.length; i += step) {
    selected.push(points[i]);
    if (selected.length >= maxPoints) break;
  }
  return selected;
};

const searchPlace = async (query) => {
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&polygon_geojson=1&limit=5`;
  
  const response = await fetch(url, {
    headers: {
      'Accept': 'application/json',
      'User-Agent': 'GeoCenter App (educational project)'
    }
  });
  
  if (!response.ok) {
    throw new Error('Ошибка при поиске');
  }
  
  return response.json();
};

const HomePage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [geojson, setGeojson] = useState(null);
  const [centroid, setCentroid] = useState(null);
  const [keyPoints, setKeyPoints] = useState([]);
  const [cityName, setCityName] = useState("");
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showInfo, setShowInfo] = useState(false);

  const clearSearchResults = () => {
    setSearchResults([]);
    setError(null);
  };

  const handleSearch = async (query) => {
    setIsLoading(true);
    setError(null);
    setSearchResults([]);
    
    try {
      const results = await searchPlace(query);
      
      if (results.length === 0) {
        setError("Населённый пункт не найден. Попробуйте другой запрос.");
        setGeojson(null);
        setCentroid(null);
        setKeyPoints([]);
        setCityName("");
        return;
      }
      
      const placeWithPolygon = results.find(r => 
        r.geojson && (r.geojson.type === 'Polygon' || r.geojson.type === 'MultiPolygon')
      );
      
      if (!placeWithPolygon) {
        setSearchResults(results);
        setError("Границы не найдены. Попробуйте выбрать из списка или уточнить запрос.");
        setGeojson(null);
        setCentroid(null);
        setKeyPoints([]);
        setCityName("");
        return;
      }
      
      setGeojson(placeWithPolygon.geojson);
      setCityName(placeWithPolygon.display_name);
      
      const centerResult = calculateGeometricCenter(placeWithPolygon.geojson);
      setCentroid(centerResult);
      
      const points = getKeyPoints(placeWithPolygon.geojson, 16);
      setKeyPoints(points);
      
    } catch (err) {
      console.error('Search error:', err);
      setError("Произошла ошибка при поиске. Проверьте соединение и попробуйте ещё раз.");
      setGeojson(null);
      setCentroid(null);
      setKeyPoints([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectResult = async (result) => {
    setSearchResults([]);
    setError(null);
    
    if (result.geojson && (result.geojson.type === 'Polygon' || result.geojson.type === 'MultiPolygon')) {
      setGeojson(result.geojson);
      setCityName(result.display_name);
      setCentroid(calculateGeometricCenter(result.geojson));
      setKeyPoints(getKeyPoints(result.geojson, 16));
    } else {
      setError("У выбранного объекта нет границ полигона.");
    }
  };

  return (
    <div className="relative w-screen h-screen overflow-hidden">
      <MapView 
        geojson={geojson}
        centroid={centroid}
        keyPoints={keyPoints}
      />

      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] w-full max-w-xl px-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowInfo(true)}
            className="flex-shrink-0 bg-white/95 backdrop-blur-sm p-3 rounded-full shadow-lg hover:bg-white transition-all"
            title="Алгоритм расчёта"
          >
            <Info className="h-5 w-5 text-sky-600" />
          </button>
          
          <div className="flex-1">
            <SearchInput 
              onSearch={handleSearch} 
              isLoading={isLoading} 
              onClearResults={clearSearchResults}
            />
          </div>
        </div>
        
        {error && (
          <div className="mt-2 p-3 bg-red-50/95 backdrop-blur-sm border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
        
        {searchResults.length > 0 && (
          <div className="mt-2 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-slate-200 overflow-hidden">
            <div className="p-2 bg-slate-50 border-b border-slate-200">
              <span className="text-xs font-medium text-slate-600">Выберите из найденных:</span>
            </div>
            <ul className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
              {searchResults.map((result, idx) => (
                <li 
                  key={idx}
                  onClick={() => handleSelectResult(result)}
                  className="p-2 hover:bg-slate-50 cursor-pointer text-sm transition-colors"
                >
                  <p className="text-slate-700 truncate">{result.display_name}</p>
                  <p className="text-xs text-slate-500">
                    {result.type} • {result.geojson ? (result.geojson.type === 'Polygon' || result.geojson.type === 'MultiPolygon' ? '✓ есть границы' : 'только точка') : 'нет границ'}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {centroid && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-[1000] w-full max-w-md px-4">
          <ResultsPanel 
            cityName={cityName}
            centroid={centroid}
            isLoading={isLoading}
          />
        </div>
      )}

      {showInfo && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/50" onClick={() => setShowInfo(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Info className="h-5 w-5 text-sky-600" />
                Алгоритм расчёта
              </h2>
              <button onClick={() => setShowInfo(false)} className="p-1 hover:bg-slate-100 rounded-full transition-colors">
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4">
              <AlgorithmInfo />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter basename="/geocenter">
        <Routes>
          <Route path="/" element={<HomePage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
